<template>

    <v-slide-y-transition>
        <v-card :color="color" flat class="ma-1">

            <div class="d-flex" :class="disableEdit ? 'pa-0' : 'pa-0'">

                <!-- DATA VARIABLE SELECTION -->
                <div>
                    <text-field-data-variable-picker
                        v-model="selectedDataVariableModel"
                        :data-variables="dataVariables"
                        :disable-edit="disableEdit"
                        @input="dataVariableChanged"
                        :color="inputColor"
                    ></text-field-data-variable-picker>
                </div>

                <!-- OPERATION SELECTION -->
                <div class="ml-2" v-if="!disableEdit">
                    <operation-selector
                        v-if="!disableEdit"
                        v-model="selectedOperationModel"
                        :data-variable="selectedDataVariableModel"
                        style="width: 200px"
                        :color="inputColor"
                    ></operation-selector>
                </div>

                <div class="flex-grow-1 align-self-center ml-2">

                    <!-- TEXT AND EMAIL -->
                    <text-input v-if="isTextType"
                                v-model="valueModel"
                                :disable-edit="disableEdit"
                                :color="inputColor"
                    ></text-input>

                    <!-- NUMERIC TYPE -->
                    <numeric v-if="isNumericType"
                             v-model="valueModel"
                             :disable-edit="disableEdit"
                             :color="inputColor"
                    ></numeric>

                    <!-- MULTI OPTION -->
                    <multi-option-selector v-if="isMultipleType"
                                           v-model="valueModel"
                                           :items="selectedDataVariableModel.options"
                                           :disable-edit="disableEdit"
                                           :color="inputColor"
                    ></multi-option-selector>

                    <!-- NUMERIC RANGE -->
                    <numeric-range v-if="isNumericRange"
                                   v-model="valueModel"
                                   :disable-edit="disableEdit"
                                   :color="inputColor"
                    ></numeric-range>

                    <date-age v-if="isDateAge && selectedOperationModel === 'age'"
                              v-model="valueModel"
                              :disable-edit="disableEdit"
                              :color="inputColor"
                              :data-variable="selectedDataVariableModel"
                    ></date-age>

                    <!-- DATE -->
                    <date v-if="isDate && selectedOperationModel !== 'age'"
                          v-model="valueModel"
                          :disable-edit="disableEdit"
                          :color="inputColor"
                    ></date>

                    <!-- DATE RANGE -->
                    <date-range v-if="isDateRange && selectedOperationModel !== 'age'"
                                v-model="valueModel"
                                :disable-edit="disableEdit"
                                :color="inputColor"
                    ></date-range>

                    <!-- LOCALE INPUT -->
                    <locale-input v-if="isLocaleType"
                                  v-model="valueModel"
                                  :disable-edit="disableEdit"
                                  :color="inputColor"
                    ></locale-input>
                </div>

                <!-- DELETE BUTTON -->
                <div class="align-self-start ml-3 mt-2">
                    <v-tooltip v-if="!disableEdit" top color="primary">
                        <template v-slot:activator="{on}">
                            <v-btn v-on="on" icon text @click="conditionRemoved">
                                <v-icon color="grey lighten-1">mdi-delete</v-icon>
                            </v-btn>
                        </template>
                        <span>Delete condition</span>
                    </v-tooltip>
                </div>

            </div>

            <div v-if="debug">
                <div class="grey--text text--whiten-1">
                    <div>Data:{{ data }}</div>
                    <div>Value: {{ valueModel }}</div>
                </div>
            </div>

        </v-card>
    </v-slide-y-transition>

</template>

<script>

import TimeStampPicker from '../../../components/UI/TimeStampPicker';
import TextFieldDataVariablePicker from '../../DataVariables/components/TextFieldDataVariablePicker';
import NumericRange from './Inputs/NumericRange';
import DateRange from './Inputs/DateRange';
import Date from './Inputs/Date';
import MultiOptionSelector from './Inputs/MultiOptionSelector';
import DataVariableChip from '../../../components/UI/DataVariableChip';
import OperationSelector from './OperationSelector';
import Numeric from "./Inputs/Numeric";
import TextInput from "./Inputs/TextInput";
import LocaleInput from "./Inputs/LocaleInput";
import DateAge from "./Inputs/DateAge";

export default {

    name: 'condition',

    components: {
        DateAge,
        TimeStampPicker,
        TextFieldDataVariablePicker,
        OperationSelector,
        TextInput,
        Date,
        Numeric,
        NumericRange,
        DateRange,
        LocaleInput,
        MultiOptionSelector,
        DataVariableChip,
    },

    props: {
        data: {
            required: true,
            type: Object
        },
        dataVariables: {
            required: true,
            type: Array
        },
        isOrGroup: {
            required: false,
            default: false,
            type: Boolean
        },
        disableEdit: {
            required: false,
            type: Boolean,
            default: false
        },
        compact: {
            required: false,
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            debug: false,
            condition: {
                id: null,
                dataVariableId: null,
                operation: null,
                value: null,
            },

            id: null,
            valueModel: null,
            selectedDataVariableModel: null,
            selectedOperationModel: 'match',
        }
    },

    watch: {

        data(data) {
            this.valueModel = data.value;
            this.selectedOperationModel = data.operation;
            this.id = data.id;
            this.findDataVariable();
        },

        selectedOperationModel(operation) {
            if (operation === "exists" || operation === "not_exist") {
                this.valueModel = null;
            }
            this.emit();
        },

        valueModel(value) {
            this.emit();
        },

        dataVariables() {
            this.findDataVariable();
        }
    },

    created() {
        this.valueModel = this.data.value;
        this.selectedOperationModel = this.data.operation;
        this.id = this.data.id;
        this.findDataVariable();
    },

    methods: {

        createLocalCopy() {
            this.findDataVariable();
        },

        dataVariableChanged() {

            this.valueModel = null;

            if (this.selectedDataVariableModel.type === "date" && this.selectedDataVariableModel.ranges.length > 0) {
                this.selectedOperationModel = 'age';
            }
        },

        findDataVariable() {
            if (this.dataVariables.length === 0) return null;

            this.selectedDataVariableModel = this.dataVariables.find((dataVariable) => {
                return dataVariable.uuid === this.data.dataVariableId;
            });
        },

        valueChanged(value) {
            this.valueModel = value;
            this.emit();
        },

        emit() {

            // Due to Async loading of data variables
            if (this.selectedDataVariableModel) {

                this.condition = {
                    id: this.data.id,
                    dataVariableId: this.selectedDataVariableModel.uuid,
                    operation: this.selectedOperationModel,
                    value: this.valueModel
                };

                if (this.condition.operation === 'exists' || this.condition.operation === 'not_exist') {
                    if (this.condition.dataVariableId && this.condition.operation) {
                        this.$emit('changed', Object.assign({}, this.condition));
                    }
                }

                if (this.condition.dataVariableId && this.condition.operation && this.condition.value) {
                    if (this.condition.value.length >= 1) {
                        this.$emit('changed', Object.assign({}, this.condition));
                    } else {
                        Bus.$emit("You must select at least one option!");
                    }
                }
            }
        },

        conditionRemoved() {
            this.$emit('deleted', this.id);
        },
    },

    computed: {

        inputColor() {
            return this.isOrGroup ? 'grey darken-1' : 'grey darken-2';
        },

        color() {
            return 'transparent';
        },

        isTextType() {
            if (!this.displayValueField) return false;

            if (this.selectedDataVariableModel.type === "text" ||
                this.selectedDataVariableModel.type === "email" ||
                this.selectedDataVariableModel.type === "phone" ||
                this.selectedDataVariableModel.type === "keyword") {
                return true;
            }
        },
        isNumericRange() {
            if (!this.displayValueField) return false;
            if (this.selectedDataVariableModel.type === "number" &&
                this.selectedOperationModel === 'range') {
                return true;
            }
        },
        isNumericType() {
            if (!this.displayValueField) return false;
            if (this.selectedDataVariableModel.type === "number" &&
                this.selectedOperationModel !== 'range') {
                return true;
            }
        },
        isMultipleType() {
            if (!this.displayValueField) return false;
            if (this.selectedDataVariableModel.type === "radio" ||
                this.selectedDataVariableModel.type === "checkbox") {
                return true;
            }
        },
        isDateAge() {
            if (!this.displayValueField) return false;
            if (!this.selectedDataVariableModel) return false;
            return this.selectedDataVariableModel.type === "date" &&
                this.selectedOperationModel === 'age';
        },
        isDate() {
            if (!this.displayValueField) return false;
            if (!this.selectedDataVariableModel) return false;
            return this.selectedDataVariableModel.type === "date" &&
                this.selectedOperationModel !== 'range';
        },
        isDateRange() {
            if (!this.displayValueField) return false;
            if (!this.selectedDataVariableModel) return false;
            return this.selectedDataVariableModel.type === "date" &&
                this.selectedOperationModel === 'range';
        },

        isLocaleType() {
            if (!this.displayValueField) return false;
            if (!this.selectedDataVariableModel) return false;

            if (this.selectedDataVariableModel.type === "locale") {
                return true;
            }
        },

        displayValueField() {
            if (this.selectedDataVariableModel && this.selectedOperationModel) {
                if (['exists', 'not_exist'].includes(this.selectedOperationModel)) {
                    return false;
                }
                return true;
            }
            return false;
        }
    },

}
</script>

